import { apiClient } from "@/lib/api-client";
import type { Receiver, Partner, ReceiverStats, ReceiverContainerFilters } from "@/types/receiver";
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from "@/types/container";

/**
 * Shipment response dal backend
 */
interface ShipmentResponse {
    container_id: string;
    container_number: string;
    company_id: string;
    shipper_company_id: string | null;
    consignee_company_id: string | null;
    freight_forwarder_id: string | null;
    status: string;
    estimated_arrival_date: string | null;
    actual_arrival_date: string | null;
    total_value_usd: number | null;
    currency: string;
    origin_country_code: string | null;
    destination_country_code: string | null;
    risk_score: number | null;
    alert_count: number;
    has_active_alerts: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Recupera tutti i shipments per la company corrente (shipper)
 * Implementa paginazione per recuperare tutti i shipments (max 500 per request)
 */
async function getShipments(shipperCompanyId: string): Promise<ShipmentResponse[]> {
    const allShipments: ShipmentResponse[] = [];
    const limit = 500; // Limite massimo dell'API
    let skip = 0;
    let hasMore = true;

    while (hasMore) {
        const params: Record<string, string | number> = {
            company_id: shipperCompanyId,
            skip,
            limit,
        };

        const shipments = await apiClient<ShipmentResponse[]>("/shipments", {
            method: "GET",
            params,
        });

        allShipments.push(...shipments);

        // Se abbiamo ricevuto meno del limite, abbiamo finito
        if (shipments.length < limit) {
            hasMore = false;
        } else {
            skip += limit;
        }
    }

    return allShipments;
}

/**
 * Mappa lo status del backend al tipo ContainerStatus
 */
function mapShipmentStatusToContainerStatus(status: string): ContainerStatus {
    // Mappatura tra ShipmentStatus del backend e ContainerStatus del frontend
    const statusMap: Record<string, ContainerStatus> = {
        DRAFT: "Draft",
        BOOKED: "Booked",
        IN_TRANSIT: "In Transit",
        ARRIVED: "Arrived",
        DELIVERED: "Delivered",
        ON_HOLD: "On Hold",
        CANCELLED: "Cancelled",
        // Legacy fallback
        DELAYED: "On Hold",
        AT_PORT: "Arrived",
    };
    return statusMap[status] || "Draft";
}

/**
 * Converte un ShipmentResponse in Container
 */
function shipmentToContainer(shipment: ShipmentResponse, shipperId: string, receiverId: string): Container {
    // Calcola progress basato su date
    let progress = 0;
    const now = new Date();
    if (shipment.estimated_arrival_date) {
        const eta = new Date(shipment.estimated_arrival_date);
        if (eta > now) {
            // Se ETA è nel futuro, calcola progress approssimativo
            const daysDiff = (eta.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
            progress = Math.max(0, Math.min(100, 100 - (daysDiff / 30) * 100));
        } else {
            progress = 100;
        }
    }

    // Determina risk level (per ora default a Low, dovrebbe venire dal backend)
    const riskLevel: RiskLevel = "Low";
    
    // Determina document status (per ora default a Pending, dovrebbe venire dal backend)
    const documentStatus: DocumentStatus = "Pending";

    return {
        id: shipment.container_id,
        shipperId,
        receiverId,
        containerNumber: shipment.container_number,
        route: {
            from: shipment.origin_country_code || "Unknown",
            to: shipment.destination_country_code || "Unknown",
        },
        progress: Math.round(progress),
        eta: shipment.estimated_arrival_date ? new Date(shipment.estimated_arrival_date) : new Date(),
        documentStatus,
        riskLevel,
        value: {
            amount: shipment.total_value_usd || 0,
            currency: shipment.currency || "USD",
        },
        status: mapShipmentStatusToContainerStatus(shipment.status),
        createdAt: new Date(shipment.created_at),
        updatedAt: new Date(shipment.updated_at),
        commodity: "avocado", // Default, dovrebbe venire dal backend
    };
}

/**
 * Response from the company-associations/receivers endpoint
 */
interface ReceiversListResponse {
    receivers: Receiver[];
    total: number;
    skip: number;
    limit: number;
}

/**
 * Response from the company-associations/partners endpoint
 */
interface PartnersListResponse {
    partners: Partner[];
    total: number;
    skip: number;
    limit: number;
}

/**
 * Recupera lista receivers associati allo shipper corrente
 * Utilizza il nuovo endpoint /company-associations/receivers che:
 * - Usa la tabella company_associations per trovare le relazioni
 * - Filtra per company_type = 'receiver'
 * - Calcola le statistiche (activeContainers, totalContainers, lastContainerDate) lato server
 */
export async function getReceivers(shipperCompanyId: string): Promise<Receiver[]> {
    const response = await apiClient<ReceiversListResponse>("/company-associations/receivers", {
        method: "GET",
        params: {
            shipper_company_id: shipperCompanyId,
            limit: 500,
        },
    });

    return response.receivers;
}

/**
 * Recupera tutti i partner associati allo shipper corrente (qualsiasi tipo)
 * Utilizza il nuovo endpoint /company-associations/partners
 */
export async function getPartners(shipperCompanyId: string, companyType?: string): Promise<Partner[]> {
    const params: Record<string, string | number> = {
        shipper_company_id: shipperCompanyId,
        limit: 500,
    };
    if (companyType) {
        params.company_type = companyType;
    }

    const response = await apiClient<PartnersListResponse>("/company-associations/partners", {
        method: "GET",
        params,
    });

    return response.partners;
}

/**
 * Recupera statistiche per un receiver specifico
 */
export async function getReceiverStats(
    shipperCompanyId: string,
    receiverId: string
): Promise<ReceiverStats> {
    // Recupera tutti i shipments tra shipper e receiver
    const shipments = await getShipments(shipperCompanyId);
    const receiverShipments = shipments.filter(
        (s) => s.consignee_company_id === receiverId
    );

    // Calcola statistiche base
    const total = receiverShipments.length;
    const inTransit = receiverShipments.filter(
        (s) => mapShipmentStatusToContainerStatus(s.status) === "In Transit"
    ).length;
    const delivered = receiverShipments.filter(
        (s) => mapShipmentStatusToContainerStatus(s.status) === "Delivered"
    ).length;
    const delayed = receiverShipments.filter(
        (s) => mapShipmentStatusToContainerStatus(s.status) === "On Hold"
    ).length;

    // Real values from backend fields
    const pendingDocuments = receiverShipments.filter(
        (s) => s.has_active_alerts
    ).length;
    const highRisk = receiverShipments.filter(
        (s) => s.risk_score !== null && s.risk_score > 0.5
    ).length;

    // Distribuzioni basate su risk_score reale
    const riskDistribution = {
        Low: receiverShipments.filter(s => s.risk_score === null || s.risk_score <= 0.25).length,
        Medium: receiverShipments.filter(s => s.risk_score !== null && s.risk_score > 0.25 && s.risk_score <= 0.5).length,
        High: receiverShipments.filter(s => s.risk_score !== null && s.risk_score > 0.5 && s.risk_score <= 0.75).length,
        Critical: receiverShipments.filter(s => s.risk_score !== null && s.risk_score > 0.75).length,
    };

    const statusDistribution = {
        Draft: receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === "Draft"
        ).length,
        Booked: receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === "Booked"
        ).length,
        "In Transit": inTransit,
        Arrived: receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === "Arrived"
        ).length,
        Delivered: delivered,
        "On Hold": receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === "On Hold"
        ).length,
        Cancelled: receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === "Cancelled"
        ).length,
    };

    const withAlerts = receiverShipments.filter(s => s.has_active_alerts).length;
    const documentDistribution = {
        Completo: total - withAlerts,
        Pending: withAlerts,
        Error: 0,
    };

    return {
        total,
        inTransit,
        delivered,
        delayed,
        pendingDocuments,
        highRisk,
        riskDistribution,
        statusDistribution,
        documentDistribution,
    };
}

/**
 * Recupera containers per un receiver specifico con filtri opzionali
 */
export async function getReceiverContainers(
    shipperCompanyId: string,
    receiverId: string,
    filters?: ReceiverContainerFilters
): Promise<Container[]> {
    // Recupera tutti i shipments tra shipper e receiver
    const shipments = await getShipments(shipperCompanyId);
    let receiverShipments = shipments.filter(
        (s) => s.consignee_company_id === receiverId
    );

    // Applica filtri lato client
    if (filters?.status) {
        receiverShipments = receiverShipments.filter(
            (s) => mapShipmentStatusToContainerStatus(s.status) === filters.status
        );
    }

    // Converti in Container
    const containers = receiverShipments.map((shipment) =>
        shipmentToContainer(shipment, shipperCompanyId, receiverId)
    );

    // Applica filtri aggiuntivi lato client
    let filtered = containers;

    if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(
            (c) =>
                c.containerNumber?.toLowerCase().includes(searchLower) ||
                c.route.from.toLowerCase().includes(searchLower) ||
                c.route.to.toLowerCase().includes(searchLower)
        );
    }

    if (filters?.riskLevel) {
        // Per ora tutti hanno riskLevel "Low", quindi questo filtro non funzionerà
        // fino a quando il backend non fornisce il risk level
        filtered = filtered.filter((c) => c.riskLevel === filters.riskLevel);
    }

    if (filters?.documentStatus) {
        // Per ora tutti hanno documentStatus "Pending", quindi questo filtro non funzionerà
        // fino a quando il backend non fornisce il document status
        filtered = filtered.filter((c) => c.documentStatus === filters.documentStatus);
    }

    // Applica paginazione
    const skip = filters?.skip || 0;
    const limit = filters?.limit || 100;

    return filtered.slice(skip, skip + limit);
}
